import type {
  PaymentMethodSchema,
  PaymentMethodSchemaWithId,
} from "@repo/validators";
import type { z } from "zod";
import { useCallback } from "react";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { PaymentMethod } from "~/providers/payment-method-provider";
import type { ManagerResult } from "~/types";
import { api } from "~/lib/api";
import { useAlertStore } from "~/stores/use-alert-store";
import { usePaymentMethodStore } from "~/stores/use-payment-method-store";
import { usePermissionManager } from "./use-permission-manager";

export const usePaymentMethodManager = () => {
  const { t } = useTranslation("common");

  const { handlePaymentMethodUpdate, handlePaymentMethodDelete } =
    usePaymentMethodUpdates();

  const { showDialog } = useAlertStore();

  const { mutate: create } = api.paymentMethod.create.useMutation({
    async onSuccess(result) {
      toast.success(
        capitalize(
          t("create_item_success", {
            item: t("PaymentMethod.payment_method"),
          }),
        ),
      );

      await handlePaymentMethodUpdate(result);
    },
  });

  const { mutate: update } = api.paymentMethod.update.useMutation({
    async onSuccess(_, { id }) {
      toast.success(
        capitalize(
          t("update_item_success", {
            item: t("PaymentMethod.payment_method"),
          }),
        ),
      );

      await handlePaymentMethodUpdate(id);
    },
    onError() {
      toast.error(
        capitalize(
          t("update_item_error", {
            item: t("PaymentMethod.payment_method"),
          }),
        ),
      );
    },
  });

  const { mutate: deletePaymentMethod } =
    api.paymentMethod.deletePaymentMethod.useMutation({
      onSuccess(_, id) {
        toast.success(
          t("delete_item_success", { item: t("PaymentMethod.payment_method") }),
        );

        handlePaymentMethodDelete(id);
      },
      onError() {
        toast.error(
          t("delete_item_fail", { item: t("PaymentMethod.payment_method") }),
        );
      },
    });

  const { setPaymentMethodFormOpen, setSelectedPaymentMethod } =
    usePaymentMethodStore();

  const { hasPermission } = usePermissionManager();

  const createPaymentMethod = async (
    data: z.infer<ReturnType<typeof PaymentMethodSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_section,
      label: t("Permissions.create_section_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof PaymentMethodSchema>>>
    >((resolve) => {
      if (!permission)
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });

      create(data, {
        onSuccess() {
          return resolve({
            data: true,
          });
        },
        onError() {
          return resolve({
            error: {
              path: "root",
              message: capitalize(
                t("create_item_error", {
                  item: t("PaymentMethod.payment_method"),
                }),
              ),
            },
          });
        },
      });
    });
  };

  const updatePaymentMethod = async (
    data: z.infer<ReturnType<typeof PaymentMethodSchemaWithId>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_section,
      label: t("Permissions.update_section_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof PaymentMethodSchemaWithId>>>
    >((resolve) => {
      if (!permission)
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });

      update(data, {
        onSuccess() {
          return resolve({
            data: true,
          });
        },
        onError() {
          resolve({
            error: {
              path: "root",
              message: capitalize(
                t("update_item_error", {
                  item: t("PaymentMethod.payment_method"),
                }),
              ),
            },
          });
        },
      });
    });
  };

  const openPaymentMethodDialog = async (paymentMethod?: PaymentMethod) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_section,
      label: t("Permissions.create_section_label"),
    });

    if (!permission) return;

    setSelectedPaymentMethod(paymentMethod);
    setPaymentMethodFormOpen(true);
  };

  const deletePaymentMethodHandler = async (paymentMethod: PaymentMethod) => {
    const result = await showDialog({
      title: t("PaymentMethod.before_delete_title", {
        payment_method: paymentMethod.name,
      }),
      message: t("PaymentMethod.before_delete_message"),
      variant: "destructive",
      buttons: [
        {
          label: t("cancel"),
          value: "cancel",
          variant: "default",
        },
        {
          label: t("delete"),
          value: "ok",
          variant: "destructive",
        },
      ],
    });

    return new Promise<boolean>((resolve) => {
      if (result === "cancel") {
        return resolve(false);
      }
      deletePaymentMethod(paymentMethod.id, {
        onSuccess() {
          return resolve(true);
        },
        onError() {
          return resolve(false);
        },
      });
    });
  };

  return {
    deletePaymentMethod: deletePaymentMethodHandler,
    createPaymentMethod,
    updatePaymentMethod,
    openPaymentMethodDialog,
  };
};

export const usePaymentMethodUpdates = () => {
  const utils = api.useUtils();

  const handlePaymentMethodUpdate = useCallback(
    async (paymentMethodId: string) => {
      const result = await utils.paymentMethod.getById.fetch(paymentMethodId);

      void utils.paymentMethod.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });
    },
    [utils.paymentMethod.getAll, utils.paymentMethod.getById],
  );

  const handlePaymentMethodDelete = useCallback(
    (paymentMethodId: string) => {
      void utils.paymentMethod.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== paymentMethodId);
      });
    },
    [utils.paymentMethod.getAll],
  );

  return {
    handlePaymentMethodUpdate,
    handlePaymentMethodDelete,
  };
};
